import React from 'react';
import * as Sentry from '@sentry/react';
import { Button } from 'antd';
import ErrorPageSvg from '../../../assets/error-page.svg';
import logoCPng from '../../../assets/images/logo-c.png';
import logoPng from '../../../assets/images/logo.png';
import { ENV_PRODUCTION } from '@common/config/environments';
import { getRuntimeContext } from 'services/error/runtimeContext';

interface SentryErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<ErrorFallbackProps>;
  showDialog?: boolean;
  level?: 'error' | 'warning' | 'info' | 'debug';
}

interface ErrorFallbackProps {
  error: unknown;
  resetError: () => void;
  eventId: string;
  componentStack?: string;
}

// Default error fallback component
const DefaultErrorFallback: React.FC<ErrorFallbackProps> = () => {

  const handleSendEmail = () => {
    window.location.href = 'mailto:processing@constlending.com?subject=Error Report&body=Please describe the issue you encountered.';
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      backgroundImage: `url(${ErrorPageSvg})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      position: 'relative'
    }}>
      {/* Header */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 200,
        width: '100%',
        height: '56px',
        padding: '0 32px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: '#f9fafb',
        borderBottom: '1px solid #dfe4eb'
      }}>
        <a 
          href='http://constlending.com/' 
          target="_blank" 
          rel="noopener noreferrer" 
          style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          <img src={logoCPng} alt="logo-c" style={{ height: '32px', width: '32px' }} />
          <img src={logoPng} alt="logo" style={{ height: '32px' }} />
        </a>
        <div style={{
          fontFamily: 'Inter',
          fontWeight: 400,
          fontSize: '16px',
          color: '#666666'
        }}>
          {`Text or call us at 203-423-3534`}
        </div>
      </div>

      {/* Content */}
      <div style={{
        position: 'relative',
        zIndex: 1,
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flex: 1,
        paddingTop: '56px'
      }}>
        <div style={{
          maxWidth: '524px',
          width: '100%',
          textAlign: 'center',
          padding: '40px 20px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '24px'
        }}>
          <h1 style={{
            fontFamily: 'Inter',
            fontWeight: 400,
            fontSize: '48px',
            lineHeight: '52px',
            color: '#0A487A',
            margin: 0
          }}>
            {`Something's gone wrong`}
          </h1>

          <div style={{
            fontFamily: 'Inter',
            fontWeight: 400,
            fontSize: '20px',
            lineHeight: '150%',
            color: '#666666'
          }}>
            {`We're working to fix this error, but in the meantime, please email your documents to`}
            <br />
            <span style={{
              fontWeight: 700,
              fontSize: '20px'
            }}>
              processing@constlending.com
            </span>
          </div>

          <Button
            type="primary"
            onClick={handleSendEmail}
            style={{
              backgroundColor: '#FFBA49',
              borderColor: '#FFBA49',
              color: '#120D00',
              borderRadius: '8px',
              padding: '12px 32px',
              height: 'auto',
              fontSize: '16px',
              fontWeight: '500',
              fontFamily: 'Inter',
              width: '100%'
            }}
          >
            Send us an email
          </Button>
        </div>
      </div>
    </div>
  );
};

// Enhanced Sentry Error Boundary with custom configuration
export const SentryErrorBoundary: React.FC<SentryErrorBoundaryProps> = ({
  children,
  fallback: FallbackComponent = DefaultErrorFallback,
  showDialog = true,
  level = 'error'
}) => {
  const handleError = (error: unknown, errorInfo: React.ErrorInfo, eventId: string) => {
    const errorObj = error as Error;
    // Enhanced error reporting with additional context
    Sentry.withScope((scope) => {
      scope.setLevel(level as any);
      scope.setTag('errorBoundary', 'true');
      scope.setTag('errorType', errorObj.name);
      scope.setContext('errorInfo', {
        componentStack: errorInfo.componentStack,
        errorBoundary: 'SentryErrorBoundary'
      });

      // Add user context if available
      const token = localStorage.getItem('token');
      if (token) {
        scope.setTag('userAuthenticated', 'true');
      } else {
        scope.setTag('userAuthenticated', 'false');
      }

      // Add browser context
      scope.setContext('browser', {
        userAgent: navigator.userAgent,
        language: navigator.language,
        platform: navigator.platform,
        cookieEnabled: navigator.cookieEnabled
      });

      // Add URL context
      scope.setContext('url', {
        href: window.location.href,
        pathname: window.location.pathname,
        search: window.location.search
      });

      // Specific handling for TypeError
      if (errorObj instanceof TypeError) {
        scope.setTag('errorCategory', 'typeError');
        scope.setContext('typeError', {
          message: errorObj.message,
          stack: errorObj.stack,
          timestamp: new Date().toISOString()
        });
      }

      // Specific handling for chunk load errors
      if (errorObj.name === 'ChunkLoadError' || errorObj.message?.includes('Loading chunk')) {
        scope.setTag('errorCategory', 'chunkLoadError');
        scope.setContext('chunkLoadError', {
          message: errorObj.message,
          timestamp: new Date().toISOString()
        });
      }

      Sentry.captureException(errorObj);
    });

    // Show dialog if enabled
    if (showDialog && ENV_PRODUCTION) {
      setTimeout(() => {
        Sentry.showReportDialog({ eventId });
      }, 1000);
    }
  };

  return (
    <Sentry.ErrorBoundary
      fallback={(errorData) => (
        <FallbackComponent
          error={errorData.error}
          resetError={errorData.resetError}
          eventId={errorData.eventId}
          componentStack={errorData.componentStack}
        />
      )}
      beforeCapture={(scope, error, errorInfo) => {
        scope.setTag('errorBoundary', 'beforeCapture');
        scope.setContext('errorInfo', {
          componentStack: typeof errorInfo === 'string' ? errorInfo : (errorInfo as any).componentStack || '',
          errorBoundary: 'beforeCapture'
        });

        const rc = getRuntimeContext();
        if (rc && rc.user) {
          scope.setUser({ id: rc.user.id, email: rc.user.email, username: rc.user.name });
        }
        if (rc && rc.loan) {
          scope.setTag('loanId', rc.loan.id || 'none');
          scope.setContext('loan', rc.loan);
        }
        if (rc && rc.modal) {
          scope.setTag('modalOpen', rc.modal.open ? (rc.modal.key || 'unknown') : 'none');
          scope.setContext('modal', rc.modal);
        }
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};

export default SentryErrorBoundary;

